'use strict';

// external imports
import React, {
    MouseEvent,
    useCallback,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {confirmPopup} from 'primereact/confirmpopup';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import Item from '~/shared/src/ci/Item';
import {ItemsAndReferencesCollection} from '~/shared/src/utilities/dataUtils';
import {getXmlForContext} from '~/shared/src/utilities/xmlExport';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';

// frontend imports
import MapSearchView from '~/frontend/src/modules/panels/MapSearchView';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    getDataRecursive,
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {setMessage} from '~/frontend/src/actions/intercom';
import deleteMapSearch from '~/frontend/src/actions/panel/mapSearch/delete';
import showDeleteSuccessDialog from '~/frontend/src/actions/panelManager/showDeleteSuccessDialog';


export type DefaultProps = {

    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton? : boolean;

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount? : number;

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip? : React.ReactNode;

    saveButtonDisabled? : boolean;

    /**
     * Should the clone Button shown in the toolbar <strong>or</strong> not.
     */
    showCloneButton? : boolean;

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton? : boolean;

    deleteButtonDisabled? : boolean;

    /**
     * Show copy XML button.
     */
    showCopyXmlButton? : boolean;

    /**
     * Show download XML button.
     */
    showDownloadXmlButton? : boolean;

    /**
     * Show reload button.
     */
    showReloadButton? : boolean;

    showModifiedHint? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    saveButtonErrorCount  : 0,
    saveButtonErrorTooltip: '',
    saveButtonDisabled    : false,
    showCloneButton       : true,
    showCopyXmlButton     : true,
    showDeleteButton      : true,
    deleteButtonDisabled  : false,
    showDownloadXmlButton : true,
    showReloadButton      : true,
    showSaveButton        : true,
    showModifiedHint      : false
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    dataContext : string;

    /**
     * The id of the root item
     */
    rootItemId? : number;

    /**
     * Event for reload button
     */
    onReloadButtonClick? : () => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const dispatch : AppDispatch = useDispatch(),
          {
              dataContext,
              deleteButtonDisabled,
              rootItemId,
              saveButtonDisabled,
              saveButtonErrorCount,
              saveButtonErrorTooltip,
              showCloneButton,
              showCopyXmlButton,
              showDeleteButton,
              showDownloadXmlButton,
              showModifiedHint,
              showReloadButton,
              showSaveButton,
              uid
          }                      = props,
          user                   = useSelector( ( state : StoreState ) => state.session?.user ),
          messageCatalogTimestamp                  = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          elusiveDataData        = useSelector( ( state : StoreState ) => uid ? state.elusiveData?.[ uid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
          language               = user
              ? user.language
              : undefined,
          globalVersions         = useMemo(
              () => {
                  return JSON.stringify( elusiveDataData ?? {} );
              },
              [
                  elusiveDataData
              ]
          ),
          rootItem               = useMemo(
              () => {
                  let retVal : Item | undefined;

                  if ( rootItemId && global.data[ dataContext ]?.items[ rootItemId ] ) {
                      retVal = global.data[ dataContext ].items[ rootItemId ];
                  }

                  return retVal
              },
              [
                  dataContext,
                  rootItemId
              ]
          ),
          data                   = useMemo(
              () => {
                  let retVal : ItemsAndReferencesCollection | undefined;

                  if ( rootItemId ) {
                      retVal = getDataRecursive( dataContext, 'CFG', rootItemId );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  globalVersions,
                  rootItemId
              ]
          ),
          itemIds                = useMemo(
              () => {
                  return data?.items.map( item => item.id );
              },
              [
                  data
              ]
          ),
          referenceIds           = useMemo(
              () => {
                  return data?.references.map( reference => reference.id );
              },
              [
                  data
              ]
          ),
          onSaveButtonClick      = useCallback(
              () => {
                  void MapSearchView.doSaveData( uid, dispatch );
              },
              [
                  uid
              ]
          ),
          onCloneButtonClick     = useCallback(
              ( event : MouseEvent<HTMLButtonElement> ) => {
                  confirmPopup( {
                      target         : event.currentTarget,
                      message        : 'Not implemnted yet!',
                      className      : 'yn-confirm-popup-notimplemented',
                      icon           : 'pi pi-exclamation-triangle yn-warning',
                      acceptLabel    : __( 'YN.base.common.yes' ),
                      acceptClassName: 'p-button p-button-outlined',
                      rejectLabel    : __( 'YN.base.common.no' ),
                      rejectClassName: 'p-button-secondary p-button-outlined mr-3'
                  } );
              },
              [
                  language
              ]
          ),
          onDeleteButtonClick    = useCallback(
              () => {
                  if ( rootItem ) {
                      void dispatch( deleteMapSearch( uid, rootItemId!, () => {
                          dispatch( showDeleteSuccessDialog( uid ) );
                      } ) );
                  }
              },
              [
                  uid,
                  rootItemId
              ]
          ),
          onCopyXmlApply         = useCallback(
              async () => {
                  await navigator.clipboard.writeText( getXmlForContext( dataContext, itemIds, referenceIds, true, true ) );

                  dispatch( setMessage(
                      uid,
                      'toast',
                      'xmlDataCopiedToClipboard',
                      {
                          severity: 'info',
                          summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                          detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
                      }
                  ) );
              },
              [
                  dataContext,
                  language,
                  itemIds,
                  messageCatalogTimestamp,
                  referenceIds,
                  uid
              ]
          ),
          onDownloadXmlApply     = useCallback(
              () => {
                  if ( rootItem ) {
                      fileDownload(
                          getXmlForContext( dataContext, itemIds, referenceIds, true, true ),
                          `MapSearch_${rootItem.key}`,
                          'xml'
                      );
                  }
              },
              [
                  dataContext,
                  itemIds,
                  referenceIds,
                  rootItem
              ]
          ),
          onReloadButtonClick    = () => {
              props.onReloadButtonClick?.();
          };

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showSaveButton ) {
                layoutComponents.left.push( {
                    type  : 'saveButton',
                    saveButtonErrorCount,
                    saveButtonErrorTooltip,
                    params: {
                        key: `${uid}_saveButton`,

                        disabled: saveButtonDisabled,
                        onClick : onSaveButtonClick
                    }
                } );
            }

            if ( showCloneButton ) {
                layoutComponents.left.push( {
                    type  : 'cloneButton',
                    params: {
                        key: `${uid}_cloneButton`,

                        onClick: onCloneButtonClick
                    }
                } );
            }

            if ( showDeleteButton ) {
                layoutComponents.left.push( {
                    type  : 'deleteButton',
                    params: {
                        key: `${uid}_deleteButton`,

                        disabled: deleteButtonDisabled,
                        confirm : true,
                        onClick : onDeleteButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showCopyXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'copyXml',
                    params: {
                        key: `${uid}_copyXmlButton`,

                        uid,

                        onExpand: () => {
                            void onCopyXmlApply();
                            return false;
                        },
                        onApply : () => {
                            void onCopyXmlApply();
                        }
                    }
                } );
            }

            if ( showDownloadXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'downloadXml',
                    params: {
                        key: `${uid}_downloadXmlButton`,

                        uid,

                        onExpand: () => {
                            onDownloadXmlApply();
                            return false;
                        },
                        onApply : onDownloadXmlApply
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showModifiedHint ) {
                layoutComponents.left.push( {
                    type  : 'custom',
                    params: {
                        key      : `${uid}_modifiedHint`,
                        className: 'flex align-items-center',
                        component: <>
                            <div className="yn-toolbar-modified-hint-icon"/>
                            <span className="ml-2 yn-secondary">{__( 'YN.base.frontend.common.modified.hint' )}</span>
                        </>
                    }
                } );
            }


            // right
            if ( showReloadButton ) {
                layoutComponents.right.push( {
                    type  : 'reloadButton',
                    params: {
                        key: `${uid}_reloadButton`,

                        confirm: () => rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema! ),
                        onClick: onReloadButtonClick
                    }
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />;
        },
        [
            language,
            messageCatalogTimestamp,
            onCloneButtonClick,
            onCopyXmlApply,
            onDeleteButtonClick,
            onDownloadXmlApply,
            onSaveButtonClick,
            saveButtonErrorCount,
            saveButtonErrorTooltip,
            showCloneButton,
            showCopyXmlButton,
            showDeleteButton,
            showDownloadXmlButton,
            showModifiedHint,
            showReloadButton,
            showSaveButton,
            uid
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'MapSearch-Toolbar';

export default Toolbar;
